import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { styled, useTheme } from '@mui/material/styles';

import useCollapseDrawer from 'hooks/useCollapseDrawer';
import useAuth from 'hooks/useAuth';
import buildSidebarConfig from 'routes/SidebarConfig';
import { buildShortcutKey } from 'utils/sidebarShortcuts';

import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';

const APP_BAR_MOBILE = 24;
const APP_BAR_DESKTOP = 52;
const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});
const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  backgroundColor: theme.palette.background.neutral,
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

export default function DashboardLayout() {
  const theme = useTheme();
  const location = useLocation();
  const { collapseClick } = useCollapseDrawer();
  const { areaPermissions = [] } = useAuth();
  const [open, setOpen] = useState(false);
  const handlePermitSound = () => {};
  const isEmbedded = useMemo(
    () => new URLSearchParams(location.search).get('embedded') === '1',
    [location.search]
  );

  const areaAccess = useMemo(() => {
    const hasScoped = Array.isArray(areaPermissions) && areaPermissions.length > 0;
    if (!hasScoped) {
      return { calendar: true, kanban: true, postIt: true, quotes: true };
    }
    return {
      calendar: areaPermissions.some((item) => item.calendar_access && item.calendar_access !== 'none'),
      kanban: areaPermissions.some((item) => item.kanban_access && item.kanban_access !== 'none'),
      postIt: areaPermissions.some((item) => item.post_it_access && item.post_it_access !== 'none'),
      quotes: areaPermissions.some((item) => item.quotes_access && item.quotes_access !== 'none')
    };
  }, [areaPermissions]);

  const sidebarConfig = useMemo(
    () =>
      buildSidebarConfig({
        showAreaCalendar: areaAccess.calendar,
        showAreaKanban: areaAccess.kanban,
        showAreaPostIt: areaAccess.postIt,
        showQuotes: areaAccess.quotes
      }),
    [areaAccess]
  );

  const handleShortcutDragStart = (event, item) => {
    const payload = {
      shortcut_key: buildShortcutKey(item),
      title: item.title,
      path: item.path,
      permission_key: item.permissions || null,
      section: null,
    };
    event.dataTransfer.setData('application/enpiso-shortcut', JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <RootStyle>
      {!isEmbedded ? (
        <>
          <DashboardNavbar
            onOpenSidebar={() => setOpen(true)}
            handlePermitSound={handlePermitSound}
            sidebarConfig={sidebarConfig}
          />
          <DashboardSidebar
            isOpenSidebar={open}
            onCloseSidebar={() => setOpen(false)}
            handlePermitSound={handlePermitSound}
            sidebarConfig={sidebarConfig}
            onShortcutDragStart={handleShortcutDragStart}
          />
        </>
      ) : null}
      <MainStyle
        onClick={handlePermitSound}
        sx={{
          transition: theme.transitions.create('margin', {
            duration: theme.transitions.duration.complex
          }),
          ...(collapseClick && !isEmbedded && {
            ml: '102px'
          }),
          ...(isEmbedded && {
            p: 0,
            pt: '0 !important',
            pb: 0,
            backgroundColor: 'transparent',
          }),
        }}
      >
        <Outlet />
      </MainStyle>

    </RootStyle>
  );
}
